/* eslint  no-unused-vars: 0 */
import React, {useEffect, useState, useCallback, useMemo, useRef} from 'react';
import {dateFixedFilters} from '../../constants/dateFilters';
import PropTypes from 'prop-types';
import {AgGridReact} from 'ag-grid-react';
import 'ag-grid-enterprise';
import {AlpineContainer} from './gridContainer';
import axios from 'axios';
import ApiUtils from '../../utils/apiUtils';
import Utils from '../../utils/utils';
import {applyConvertToBooleanSetFilters, applySetFilterValueTransforms, resolveColumnDefsForFilter} from '../../utils/gridFilterModelUtils';
import {useSearchParams} from 'react-router-dom';
import {useGridLayout} from '../../contextProviders/gridLayoutContext';
import {useSelector} from 'react-redux';
import {readFilterModel, saveFilterModel} from '../../utils/gridFilterSession';
import {readGridLayout} from '../../utils/gridLayoutStorage';
import {orderColumnDefs} from './orderColumnDefs';
import { logRoles } from '@testing-library/react';
//import { ClosedCaptionDisabledOutlined } from '@mui/icons-material';
const _ = require('lodash');
require('./grid-styles.scss');

const MyPaginatedGrid = (props) => {
  const FILTER_TEXT_INPUT_SELECTOR = 'input.ag-input-field-input';

  //console.log('inside MyPageinationGrid');

  const [searchParams, setSearchParams] = useSearchParams();
  /**
   * The user's saved column layout for this grid, held server side so it follows
   * them between browsers. `props.ls_name` is the grid's key.
   */
  const {layout: savedLayout, saveLayout} = useGridLayout(props.ls_name);
  const columnApiRef = useRef(null);
  /**
   * The column state last pushed to, or pulled from, the grid. Without it every
   * save would bounce back through the layout state and re-apply itself to the
   * grid the user is still dragging in.
   */
  const syncedLayoutRef = useRef(null);
  /** Filter models are stored per user so a sign out and back in does not inherit them. */
  const userId = useSelector((state)=> state.user.id);
  /**
   * The column order used to build the grid, read straight from the browser cache so
   * it is available on the very first render.
   *
   * It is deliberately captured once and never updated. Handing AG Grid a new
   * columnDefs array after the grid is live makes it rebuild its columns, and a grid
   * with a pinned `lockPosition` column (allUsersGrid pins USER NAME) then has its
   * own locking fight the incoming order: the pinned container keeps a width that no
   * longer matches what is in it, so cells overlap and every later column renders one
   * header out of step. Order changes after mount go through applySavedLayout instead.
   */
  const initialLayoutRef = useRef(undefined);
  if (initialLayoutRef.current === undefined) {
    initialLayoutRef.current = readGridLayout(userId, props.ls_name);
  }
  const initialLayout = initialLayoutRef.current;
  //const [dragStart, setDragStart] = useState(false);
  const [gridApi, setGridApi] = useState(null);
  /**
   * The datasource below is memoized without `gridApi` in its dependencies, so the closure
   * captures whatever the state was when the memo last ran - null on first mount. Grids
   * whose memo deps never change therefore never called showLoadingOverlay/hideOverlay at
   * all. Read the api through this ref instead so it is always the live one.
   */
  const gridApiRef = useRef(null);
  const [tempGridApi, setTempGridApi] = useState(null);
  //const [currentFilterCol, setCurrentFilterCol] = useState(null);
  const [currentPage, setCurrentPage] = useState(props.pagination.currentPage);
  const [itemsPerPage, setItemsPerPage] = useState(props.pagination.itemsPerPage);
  const portalPaginationPageRef = useRef(1);
  portalPaginationPageRef.current = Math.max(1, Number(props.pagination?.currentPage ?? currentPage) || 1);
  const setPaginationCurrentPageRef = useRef(props.pagination.setCurrentPage);
  setPaginationCurrentPageRef.current = props.pagination.setCurrentPage;
  const currUrlSearchParams = useRef(props.defFilterParams?.[0]);
  const additionalFilters = useRef(props.additionalFilters);
  const globalSearchFiltersRef = useRef({});
  const isMountedRef = useRef(true);
  const latestRequestIdRef = useRef(0);
  /** Last portal page number synced to the grid; used to purge only on real page changes and avoid repeat API calls. */
  const lastSyncedPaginationPageRef = useRef(null);
  /** False until shortly after gridApi is set, so default/programmatic filters do not force page 1. */
  const filterChangePageResetEnabledRef = useRef(false);
  const prevExternalFiltersSignatureRef = useRef(null);
  const prevGlobalSearchFiltersSignatureRef = useRef(null);

  const externalFiltersSignature = useMemo(
    () => JSON.stringify({
      additional: props.additionalFilters ?? {},
      global: props.globalFilters ?? {},
    }),
    [props.additionalFilters, props.globalFilters]
  );
  const globalSearchFiltersSignature = useMemo(
    () => JSON.stringify(props.globalSearchFilters ?? {}),
    [props.globalSearchFilters]
  );

  const isGlobalSearch = useRef(false);

  const isAgFilterTextInput = useCallback((element) => {
    if (!(element instanceof HTMLInputElement)) {
      return false;
    }

    if (element.type && element.type !== 'text' && element.type !== 'search') {
      return false;
    }

    return !!(
      element.matches(FILTER_TEXT_INPUT_SELECTOR) &&
      (
        element.closest('.ag-floating-filter-body') ||
        element.closest('.ag-filter-body-wrapper') ||
        element.closest('.ag-set-filter')
      )
    );
  }, []);

  const isAgSetFilterTextInput = useCallback((element) => {
    return isAgFilterTextInput(element) && !!element.closest('.ag-set-filter');
  }, [isAgFilterTextInput]);

  const getApplyButtonForFilterElement = useCallback((element) => {
    if (!(element instanceof HTMLElement)) {
      return null;
    }

    const filterRoot = element.closest('.ag-filter');
    if (!filterRoot) {
      return null;
    }

    return (
      filterRoot.querySelector('.ag-filter-apply-panel-button') ||
      Array.from(filterRoot.querySelectorAll('.ag-standard-button')).find(
        (button) => button.textContent?.trim().toLowerCase() === 'apply'
      ) ||
      null
    );
  }, []);

  const isApplyButtonElement = useCallback((element) => {
    if (!(element instanceof HTMLElement)) {
      return false;
    }

    if (!element.closest('.ag-filter')) {
      return false;
    }

    return (
      element.classList.contains('ag-filter-apply-panel-button') ||
      element.textContent?.trim().toLowerCase() === 'apply'
    );
  }, []);

  const closeFilterPopupForElement = useCallback((element) => {
    if (!(element instanceof HTMLElement)) {
      return;
    }

    if (gridApi?.hidePopupMenu) {
      gridApi.hidePopupMenu();
    }

    const popup = element.closest('.ag-popup');
    if (popup) {
      const closeButton = popup.querySelector('.ag-menu-close-button, .ag-dialog-close-button');
      if (closeButton instanceof HTMLElement) {
        closeButton.click();
        return;
      }
    }

    const escapeEventOptions = {
      bubbles: true,
      cancelable: true,
      key: 'Escape',
      code: 'Escape',
      keyCode: 27,
      which: 27,
    };

    (popup || element).dispatchEvent(new KeyboardEvent('keydown', escapeEventOptions));
    (popup || element).dispatchEvent(new KeyboardEvent('keyup', escapeEventOptions));
    document.dispatchEvent(new MouseEvent('mousedown', {bubbles: true}));
  }, [gridApi]);

  const clickApplyAndClose = useCallback((element) => {
    const applyButton = getApplyButtonForFilterElement(element);
    if (!applyButton) {
      return false;
    }

    applyButton.click();
    setTimeout(() => closeFilterPopupForElement(element), 0);
    return true;
  }, [closeFilterPopupForElement, getApplyButtonForFilterElement]);

  const clickApplyWithoutClose = useCallback((element) => {
    const applyButton = getApplyButtonForFilterElement(element);
    if (!applyButton) {
      return false;
    }

    applyButton.click();
    return true;
  }, [getApplyButtonForFilterElement]);

  const triggerEnterOnFilterInput = useCallback((input) => {
    const keyboardEventOptions = {
      bubbles: true,
      cancelable: true,
      key: 'Enter',
      code: 'Enter',
      keyCode: 13,
      which: 13,
    };

    input.dispatchEvent(new KeyboardEvent('keydown', keyboardEventOptions));
    input.dispatchEvent(new KeyboardEvent('keyup', keyboardEventOptions));
  }, []);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!gridApi) {
      filterChangePageResetEnabledRef.current = false;
      prevExternalFiltersSignatureRef.current = null;
      prevGlobalSearchFiltersSignatureRef.current = null;
      return;
    }
    filterChangePageResetEnabledRef.current = false;
    const timerId = window.setTimeout(() => {
      filterChangePageResetEnabledRef.current = true;
    }, 450);
    return () => window.clearTimeout(timerId);
  }, [gridApi]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const target = event.target;
      if (
        event.key !== 'Enter' ||
        !isAgSetFilterTextInput(target)
      ) {
        return;
      }

      const didApply = clickApplyWithoutClose(target);
      if (!didApply) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
    };

    const handleFocusIn = (event) => {
      const target = event.target;
      if (!isAgFilterTextInput(target)) {
        return;
      }

      target.setAttribute('autocomplete', 'off');
      target.dataset.agFilterInitialValue = target.value;
    };

    const handleFocusOut = (event) => {
      const target = event.target;
      if (!isAgFilterTextInput(target)) {
        return;
      }

      const nextFocusedElement = event.relatedTarget;
      if (nextFocusedElement instanceof HTMLElement &&
          (nextFocusedElement.closest('.ag-standard-button') ||
           nextFocusedElement.closest('.ag-set-filter-list'))) {
        return;
      }

      const previousValue = target.dataset.agFilterInitialValue ?? '';
      delete target.dataset.agFilterInitialValue;

      if (target.value === previousValue) {
        return;
      }

      if (isAgSetFilterTextInput(target)) {
        if (clickApplyAndClose(target)) {
          return;
        }
      }

      triggerEnterOnFilterInput(target);
    };

    const handleClick = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }

      const applyButton = target.closest('.ag-standard-button, .ag-filter-apply-panel-button');
      if (!isApplyButtonElement(applyButton)) {
        return;
      }

      setTimeout(() => closeFilterPopupForElement(applyButton), 0);
    };

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('click', handleClick, true);
    document.addEventListener('focusin', handleFocusIn, true);
    document.addEventListener('focusout', handleFocusOut, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('focusin', handleFocusIn, true);
      document.removeEventListener('focusout', handleFocusOut, true);
    };
  }, [clickApplyAndClose, clickApplyWithoutClose, closeFilterPopupForElement, isAgFilterTextInput, isAgSetFilterTextInput, isApplyButtonElement, triggerEnterOnFilterInput]);

  //console.log('My PaginatedGrid props');
  //console.log(props);
  //console.log(props.fieldsToFilterOnEnter);


  const defaultColDef = useMemo( ()=> ({
    flex: 1,
    filter: true,
    resizable: true,
    sortable: false,
    floatingFilter: true,
    suppressMenu: true,
    //debounceMs: 100,
  }), []);

  const formatFilterModel = (model, columnDefsOrGrid = null) => {

    // AG Grid's getFilterModel() (and params.request.filterModel) hands back the filters' own
    // applied model objects, not copies. Everything below rewrites values for the API payload,
    // so mutating in place corrupts the live filter state -- the set filter's floating filter
    // then renders "(0) " because the rewritten API values no longer match its value list.
    model = _.cloneDeep(model);

    for(const key of Object.keys(model)) {
      if (key === "cityInspection.requirement") {
        if (model["cityInspection.requirement"]["cityInspection.needsLadder"]) {
          model["cityInspection.needsLadder"] = model["cityInspection.requirement"]["cityInspection.needsLadder"];
        }
        model["cityInspection.requirement"] = model["cityInspection.requirement"]["cityInspection.requirement"];
      }
    }

    const defs = resolveColumnDefsForFilter(columnDefsOrGrid);
    applyConvertToBooleanSetFilters(model, defs);
    applySetFilterValueTransforms(model, defs);

    //console.log('new model');
    //console.log(model);

    for(const value of Object.values(model)) {
      if(value.dateFrom) value.dateFrom = Utils.formatLocalTimeToIsoString(value.dateFrom, true);
      if(value.dateTo) value.dateTo = Utils.formatLocalTimeToIsoString(value.dateTo, true);
    }

    return model;
  };

  /**
   * Pushes the saved layout onto the live grid, skipping a state the grid already
   * has - without that, our own save bounces back through the layout state and
   * re-applies itself into a grid the user may still be dragging in.
   */
  const applySavedLayout = useCallback((columnApi) => {
    if (!columnApi || !savedLayout) return;

    const serialized = JSON.stringify(savedLayout);
    if (syncedLayoutRef.current === serialized) return;

    syncedLayoutRef.current = serialized;
    columnApi.applyColumnState({state: savedLayout, applyOrder: true});
  }, [savedLayout]);
  // onGridReady is memoised without savedLayout, so it reads the latest through a ref.
  const applySavedLayoutRef = useRef(applySavedLayout);
  applySavedLayoutRef.current = applySavedLayout;

  const onColumnMoved = useCallback((params) => {
    const columnState = params.columnApi.getColumnState();
    syncedLayoutRef.current = JSON.stringify(columnState);
    saveLayout(columnState);
  }, [saveLayout]);

  const onCellDoubleClicked = useCallback((params) => {
    if(props.onCellDoubleClicked) {
      props.onCellDoubleClicked(params);
    }
  }, [props.onCellDoubleClicked]);

  // Thanh 05/01/24
  const onTempGridReady = useCallback((params) => {
    setTempGridApi(params.api);

    if (props.registerTempGrid)
    {
      props.registerTempGrid(params, props.fetchParams);
    }    
  }, [props.fetchParams, props.registerTempGrid]);

  const onGridReady = useCallback((params) => {
    const hasDefaultFilters = Boolean(props.defFilterParams) &&
      Array.isArray(props.defFilterParams) &&
      props.defFilterParams.length === 2;

    // A grid opened with default filters was navigated to for those filters (a
    // dashboard tile, say), so they win over whatever the user last filtered on.
    // Everything else picks its filters back up, which is what makes opening a
    // record and coming back not lose them.
    //
    // This has to run first, before setGridApi and registerGrid. gridReady fires
    // outside a React batch, so each of those re-renders synchronously, and the
    // second one flushes the effect that attaches the datasource - the grid then
    // has loading rows whose React row components have not run their mount
    // effects yet. A filter change at that point purges the store, nulls those
    // rows' indexes, and the very next render runs the pending effects against
    // them: "Cannot read properties of null (reading 'toString')" in
    // getRowIndexString, caught by the error boundary. Switching inspection views
    // remounts the grid with the previous view's filters saved, so it hit every
    // time. With no datasource yet the filter change is a no-op for the row model,
    // and the first request goes out already filtered.
    if(!hasDefaultFilters) {
      const savedFilterModel = readFilterModel(userId, props.ls_name);
      if(savedFilterModel) {
        params.api.setFilterModel(savedFilterModel);
      }
    }

    // The saved layout goes in here too, for the same reason: it carries each
    // column's sort, and a sort change purges the store just as a filter change
    // does. Applied now it also means the first rows render in the saved order,
    // and the first request goes out already sorted. The effect below covers a
    // layout that arrives from the server afterwards.
    applySavedLayoutRef.current(params.columnApi);

    setGridApi(params.api);
    gridApiRef.current = params.api;
    columnApiRef.current = params.columnApi;

    if(hasDefaultFilters){
      try{
        setTimeout(()=> {
          var filterComponent = params.api.getFilterInstance(props.defFilterParams[0]);
          filterComponent.setModel({
            filterType: 'set',
            values:[props.defFilterParams[1]]
          });
          params.api.eventService.addEventListener('filterModified', (e) => {
            removeQueryParams(currUrlSearchParams.current);
            currUrlSearchParams.current = null;
          });
          filterComponent.onFilterChanged();
        },150);

      }catch (e){}
    }

    props.registerGrid(params);
  }, [props.defFilterParams, props.ls_name, props.registerGrid, userId]);

  useEffect(() => {
    if (!gridApi) return;
    applySavedLayoutRef.current(columnApiRef.current);
  }, [gridApi, savedLayout]);

  const onFilterChanged = useCallback((params) => {
    // Saving an empty model drops the stored entry, so clearing a grid's filters
    // is all the clear-all control has to do to forget them.
    saveFilterModel(userId, props.ls_name, params.api.getFilterModel());

    if (!filterChangePageResetEnabledRef.current) {
      return;
    }
    portalPaginationPageRef.current = 1;
    setPaginationCurrentPageRef.current(1);
  }, [props.ls_name, userId]);

  useEffect(() => {
    if (!gridApi) {
      return;
    }
    const filtersChanged =
      prevExternalFiltersSignatureRef.current !== null &&
      prevExternalFiltersSignatureRef.current !== externalFiltersSignature;
    prevExternalFiltersSignatureRef.current = externalFiltersSignature;

    additionalFilters.current = props.additionalFilters;

    if (filtersChanged) {
      portalPaginationPageRef.current = 1;
      setPaginationCurrentPageRef.current(1);
    }

    gridApi.onFilterChanged();
  }, [gridApi, externalFiltersSignature, props.additionalFilters]);

  useEffect(() => {
    if (!gridApi) {
      return;
    }

    const filtersChanged =
      prevGlobalSearchFiltersSignatureRef.current !== null &&
      prevGlobalSearchFiltersSignatureRef.current !== globalSearchFiltersSignature;
    prevGlobalSearchFiltersSignatureRef.current = globalSearchFiltersSignature;

    globalSearchFiltersRef.current = props.globalSearchFilters ?? {};
    isGlobalSearch.current = Object.keys(globalSearchFiltersRef.current).length > 0;

    if (filtersChanged) {
      portalPaginationPageRef.current = 1;
      setPaginationCurrentPageRef.current(1);
    }

    gridApi.onFilterChanged();
  }, [gridApi, globalSearchFiltersSignature, props.globalSearchFilters]);

  //},[props.additionalFilters, props.globalFilters]);
  const portalFiltersToAgGridModel = ()=> {

    //console.log('portal filter to AG Grid');
    //console.log('additional filter - current');
    //console.log(additionalFilters.current);

    const agGridFilters = {};
    if(!additionalFilters.current) return agGridFilters;

    for (const [field, filter] of Object.entries(additionalFilters.current)) {

      //console.log('current filter type');
      //console.log(filter);

      if (filter?.filterType == 'date') {
        if (fiilter?.type === 'before') {
          agGridFilters[field] = {...filter, type: 'lessThan'};
        }
        if (filter?.type === 'thisMonth' || filter?.type === 'lastMonth') {
          agGridFilters[field] = {...filter, type: 'inRange'};
        }
        else if (filter?.type === 'today') {
          agGridFilters[field] = {...filter, type: 'equals'};
        }
        else if (filter?.type === 'fromTodayOn') {
          agGridFilters[field] = {...filter, type: 'greaterThanOrEquals'};
        }
        else {
          agGridFilters[field] = filter;
        }
      }
      else {
        if(filter.param === 'empty' || filter.param === 'notEmpty') {
          agGridFilters[field] = {
            type: filter.param,
            filterType: filter.type
          };
        } else if(filter.value && filter.value.length !== 0){
          agGridFilters[field] = {
            type: filter.param,
            filterType: filter.type,
            filter: filter.value
          };
        }  
      }
    }

    //console.log('portal filter - agGridFilter');
    //console.log(agGridFilters);

    return agGridFilters;

  };

  const getServerSideDataSource = useMemo(()=>(fetchParams, companyId, limit = 10) => {

    const api = fetchParams.url;
    const apiSource = fetchParams.apiSource;
    const options = fetchParams.options;

    return {
      getRows: async (params) => {
        /**
         * With `serverGroup`, AG Grid asks for the groups first (no groupKeys) and then for the
         * rows of each group it opens. Those child requests are paged inside their group and
         * must not touch the portal's page counter, which counts the groups. They can also run
         * side by side (several open groups), so they don't take part in latest-request-wins.
         */
        const groupKeys = params.request?.groupKeys ?? [];
        const isGroupChildRequest = Boolean(props.serverGroup) && groupKeys.length > 0;
        const requestId = isGroupChildRequest ? null : ++latestRequestIdRef.current;
        let filterModel;
        if(currUrlSearchParams.current){
          const instance = params.api.getFilterInstance(currUrlSearchParams.current);
          filterModel = {[currUrlSearchParams.current]: instance.appliedModel};

          //console.log('initial default filter');
          //console.log(filterModel);

        } else {
          filterModel = params.request?.filterModel || {};
        }

        //console.log('sort model');
        //console.log(params.request?.sortModel);


        const fetch = async ()=> {
          if(gridApiRef.current && !isGroupChildRequest) gridApiRef.current.showLoadingOverlay();

          const limitSafe = Math.max(1, Number(limit) || 1);
          const startRow = Number(params.request?.startRow ?? 0);
          const pageFromGrid = Math.floor(startRow / limitSafe) + 1;
          const portalPage = Math.max(1, Number(portalPaginationPageRef.current) || 1);
          const page = isGroupChildRequest ? pageFromGrid : Math.max(pageFromGrid, portalPage);
          filterModel = {...formatFilterModel(filterModel, props.columnDefs),...portalFiltersToAgGridModel()};

          //console.log('global search filters');
          //console.log(globalSearchFiltersRef.current);

          if (isGlobalSearch.current) {
            filterModel = {...filterModel,  ...globalSearchFiltersRef.current};
          }
          else {
            if (props.globalFilters) {

              //console.log('globgal filters');
              //console.log(props.globalFilters);

              //console.log('filter model');
              //console.log(filterModel);

              if (!('createdAt' in filterModel)) {
                filterModel = {...filterModel,  ...props.globalFilters};
              }
            }
          }

        //,...[props.globalFilters]
          // Thanh 02/024/24 
          //console.log('filter model #5');
          //console.log(filterModel);
          //console.log('respkey => ', fetchParams.respKey);
         
          // if (filterModel['jobNumber'] && fetchParams.respKey == 'stJobs') {
          //   if (filterModel['jobNumber'].filter.toString().length < 6) {
          //     return undefined;
          //   }
          // }
          // if (filterModel['jobNumber'] && fetchParams.respKey != 'stJobs') {
          //   if (filterModel['jobNumber'].filter.toString().length < 7) {
          //     return undefined;
          //   }
          // }

          
          //console.log('after filter modal company id =>' + companyId);

          let requestParams = {limit: limit, page: page, filterModel: filterModel, sortModel: params.request?.sortModel};


          if (options?.showNotSyncedToCRM) {
            requestParams = {...requestParams, showNotSyncedToCRM: options.showNotSyncedToCRM};
          }
          if(options?.status) {
            requestParams = {...requestParams, status: options.status};
          }
          if(options?.tenantId) {
            requestParams = {...requestParams, tenantId: options.tenantId} 
          }
          if(options?.companyId) {
            requestParams = {...requestParams, companyId: options.companyId} 
          }
          if(options?.selectedDate) {
            requestParams = {...requestParams, selectedDate: options.selectedDate} 
          }
          if(options?.isForContractor) {
            requestParams = {...requestParams, isForContractor: options.isForContractor} 
          }
          if (options?.invPaymentStatus !== undefined) {
            requestParams.invPaymentStatus = options.invPaymentStatus;
          }
          if(options?.companyIds) {
            requestParams = {...requestParams, companyIds: options.companyIds} 
          }
          if (props.serverGroup) {
            requestParams = {...requestParams, ...props.serverGroup.requestParams};
            if (isGroupChildRequest) {
              requestParams[props.serverGroup.keyParam] = groupKeys[groupKeys.length - 1];
            }
          }
          
          if(companyId) {
            requestParams = {...requestParams, companyId: companyId};
          }
          //console.log('global ssearch ?');
          //console.log(isGlobalSearch.current);

          if (isGlobalSearch.current) {
            requestParams = {...requestParams, isGlobalSearch: true};
          }
          //isGlobalSearch.current = false;

          //console.log('thanh pham => requestParams');
          //console.log(requestParams);

          let url = `${api}`;

          const authToken = await ApiUtils.getAccessToken();
          
          let userList;

          
          if(apiSource === 'iPermitPro') {
            userList = await axios.get(url, {params: requestParams, headers: {Authorization: authToken}});
            return userList.data.data;
          }

          userList = await axios.get(url, {params: requestParams, headers: {Authorization: authToken}});
          return userList.data.data;
   
          //console.log(`finish gettng userlist => ${new Date()}`);

          // Thanh 12/02/23
          //console.log('grid data');
          //console.log(`end getting uselist => ${new Date()}`);
          //console.log(url);
          //console.log(userList.data.data);
          //return userList.data.data;
        };

        //props.pagination.setCurrentPage(undefined);
        //props.pagination.setTotalPageCount(undefined);
        //props.pagination.setTotalItemsCount(undefined);

        const isStale = () => !isMountedRef.current ||
          (!isGroupChildRequest && requestId !== latestRequestIdRef.current);

        fetch()
          .then((data)=> {
            if (isStale()) {
              return;
            }

            //console.log('data from URL');
            //console.log(data);

            if (data === undefined) {
              return;
            }
            //console.log('end of data');


            const rawList = data[props.fetchParams.respKey];
            const list = Array.isArray(rawList) ? rawList : [];
            const modifiedList = list.map((el) => {
              el = props.modifyDataElement ? props.modifyDataElement(el) : el;
              return el;
            });

            if (isGroupChildRequest) {
              // The rows of one open group: its own loading row covers it, and the page
              // counter and totals belong to the group level.
              const childTotal = Number(data?.totalRecords);
              params.success({
                rowData: modifiedList,
                rowCount: Number.isFinite(childTotal) ? childTotal : modifiedList.length,
              });
              return;
            }

            if(gridApiRef.current) gridApiRef.current.hideOverlay();

            //console.log('modifiedList', modifiedList);

            if(gridApiRef.current && (!modifiedList || modifiedList.length === 0)) {
              gridApiRef.current.showNoRowsOverlay();
            }

            // Do not call setCurrentPage here: after setServerSideDatasource the first
            // getRows is often for page 1, which would overwrite MUI/URL page (e.g. 5 → 1).
            props.pagination.setTotalPageCount(data.pagesCount);
            props.pagination.setTotalItemsCount(data.totalRecords);


            if (props.onServerFetchComplete) {
              props.onServerFetchComplete(data);
            }

            const parsedTotalRecords = Number(data?.totalRecords);
            const totalRecords = Number.isFinite(parsedTotalRecords) ? parsedTotalRecords : 0;
            const requestedStartRow = Number(params.request?.startRow ?? 0);
            const requestedEndRow = Number(params.request?.endRow ?? requestedStartRow + limitSafe);
            const isUnderfilledBlock = modifiedList.length < Math.max(0, requestedEndRow - requestedStartRow);
            // Grouped, the server sends every group at once and totalRecords counts the rows
            // inside them, so the row count here is just the groups.
            const effectiveRowCount = (isUnderfilledBlock || props.serverGroup)
              ? requestedStartRow + modifiedList.length
              : totalRecords;

            params.success({rowData: modifiedList, rowCount: effectiveRowCount});
          })
          .catch((e)=> {
            if (isStale()) {
              return;
            }
            if (isGroupChildRequest) {
              params.fail();
              props.onFetchFail(e);
              return;
            }
            if(gridApiRef.current) {
              gridApiRef.current.showNoRowsOverlay();
            }
            //console.log('FAILED TO FETCH');
            //console.log('error', e);
            params.fail();
            props.pagination.setCurrentPage(0);
            props.pagination.setTotalPageCount(0);
            props.pagination.setTotalItemsCount(0);
            props.onFetchFail(e);
            if (props.onServerFetchComplete) {
              props.onServerFetchComplete(null);
            }
          });
      }
    };
  },[props.additionalFilters, props.globalFilters, props.globalSearchFilters, props.columnDefs, props.fetchParams, props.serverGroup]);
  //},[props.additionalFilters, props.globalFilters]);

  useEffect(() => {

    
    //console.log('inside paginatedGridWrapper set server side datasource - useEffect');
    
    if(gridApi) {

      const dataSource = getServerSideDataSource(props.fetchParams, props.companyId, itemsPerPage);      
      gridApi.setServerSideDatasource(dataSource);
    }
  }, [gridApi, itemsPerPage, props.fetchParams, props.companyId, props.refreshTimestamp]);


 // [gridApi, itemsPerPage, props.fetchParams.url, props.fetchParams.apiSource, props.companyId, props.refreshTimestamp]);

  //  }, [gridApi, itemsPerPage, props.fetchParams.url, props.companyId, props.refreshTimestamp]);

  useEffect(() => {

    //console.log('inside paginatedGridWrapper set current page - useEffect'); 
    //console.log('props.pagination.currentPage', props.pagination.currentPage);

    setCurrentPage(props.pagination.currentPage);
    setItemsPerPage(props.pagination.itemsPerPage);


  }, [props.pagination.currentPage, props.pagination.itemsPerPage]);

  // const clearCacheForInactivePages = () => {
  //   if (gridApi) {
  //     //const currentPageIndex = gridApi.paginationGetCurrentPage();
  //     //setActivePage(currentPageIndex); // Update active page stat
  //     console.log('current page = '  + currentPage);
  //     gridApi.forEachNode(node => {
  //       // Check if the row's page index is not the active page

  //       console.log('node');
  //       console.log(node);
  //       console.log('node page');
  //       console.log(Math.floor(node.rowIndex / gridApi.paginationGetPageSize()) + 1);

  //       if (Math.floor(node.rowIndex / gridApi.paginationGetPageSize()) + 1 !== currentPage) {
  //         node.setData(null); // Clear data for inactive rows
  //       }
  //     });
  //   }
  // };

  useEffect(() => {
    if (!gridApi) {
      lastSyncedPaginationPageRef.current = null;
      return;
    }

    const pageNum = Number(currentPage) || 1;
    const pageIndex = Math.max(0, pageNum - 1);
    const hadSyncedBefore = lastSyncedPaginationPageRef.current != null;
    const pageNumChanged = hadSyncedBefore && lastSyncedPaginationPageRef.current !== pageNum;

    if (pageNumChanged && typeof gridApi.refreshServerSideStore === 'function') {
      gridApi.refreshServerSideStore({purge: true});
    }

    lastSyncedPaginationPageRef.current = pageNum;

    const agPage = typeof gridApi.paginationGetCurrentPage === 'function'
      ? gridApi.paginationGetCurrentPage()
      : -1;
    if (agPage !== pageIndex) {
      gridApi.paginationGoToPage(pageIndex);
    }
  }, [currentPage, gridApi]);


  const orderedColumnDefs = useMemo(() => {
    const applyOverrides = (defs) => {
      if (!defs || !props.colDefOverrides) return defs;
      return defs.map((def) => ({ ...def, ...props.colDefOverrides }));
    };

    if(!props.ls_name) return applyOverrides(props.columnDefs);

    return applyOverrides(orderColumnDefs(props.columnDefs, initialLayout));
  }, [props.columnDefs, props.ls_name, props.colDefOverrides, initialLayout]);

  const gridDefaultOptions = useMemo(() => ({
    reactUi: 'true',
    className: 'ag-theme-alpine',
    animateRows: 'true',
    onColumnMoved: onColumnMoved,
    defaultColDef: defaultColDef,
    enableCellTextSelection: true,
    rowSelection: 'multiple',
    suppressHorizontalScroll: false,
    // A real spinner rather than bare text, so a slow page/filter fetch reads as loading.
    // <Typography> is not an HTML tag, so the old template only ever rendered its text.
    // ag-icon-loading is AG Grid's own spinner (same one its loading rows use), so it
    // tracks the theme. AG Grid substitutes [LOADING...] with the localised loadingOoo text.
    overlayLoadingTemplate:
      '<span class="ag-overlay-loading-center"><span class="ag-icon ag-icon-loading" role="presentation"></span>[LOADING...]</span>',
    overlayNoRowsTemplate: '<Typography> There are no records!</Typography>',
    headerHeight: 30,
    rowHeight: 20,
    ...props.gridOptionsOverrides,
  }), [defaultColDef, onColumnMoved, props.gridOptionsOverrides]);
  const removeQueryParams = (p) => {
    const param = searchParams.get(p);

    if (param) {
      // 👇️ delete each query param
      searchParams.delete(p);

      // 👇️ update state after
      setSearchParams(searchParams);
    }
  };

  function onFirstDataRendered(params) {
    params.api.forEachNode(function (node) {
      node.setExpanded(node.id === '1');
    });
  }


  // useEffect(() => {

  //   if(!gridApi) return;

  //   const handleKeyPress = (e) => {
  //     if (e.key === 'Enter') {

  //       console.log('enter key pressed');

  //       gridApi.onFilterChanged(); // Apply the filter when "Enter" is pressed
  //     }
  //   };

  //   const addEnterKeyListener = () => {

  //     console.log('set enter key listener');
 
  //     const filterInstance = gridApi.getFilterInstance();

  //     console.log('filter instance');
  //     console.log(filterInstance);

  //     const filterInput = filterInstance?.gui?.querySelector('input');

  //     console.log('filter input');
  //     console.log(filterInput);

  //     if (filterInput) {
  //       filterInput.addEventListener('keypress', handleKeyPress);
  //     }
  //   };

  //   gridApi.addEventListener('filterOpened', addEnterKeyListener);

  //   return () => {
  //     gridApi.removeEventListener('filterOpened', addEnterKeyListener);
  //   };
  // }, []);

  
  const gridOptions = {
    suppressDragLeaveHidesColumns: true,

    // onCellKeyPress: (event) => {

    //   console.log('inside oncellkeypress');
    //   console.log(event);

    //   console.log(props.fieldsToFilterOnEnter);

    //   if (!props.fieldsToFilterOnEnter) {

    //     console.log('no filter on enter');

    //     return;
    //   }
    //   // Check if Enter key was pressed and if the active cell is in the specific column
    //   if (event.event.key === 'Enter' && props.fieldsToFilterOnEnter.includes(event.colDef.field)) {
        
    //     console.log ('about to filter after "enter" key pressed');

    //     const filterInstance = event.api.getFilterInstance(searchColumnField);
  
    //     if (filterInstance) {
    //       // Set filter model based on current cell value
    //       filterInstance.setModel({
    //         type: 'contains',
    //         filter: event.value, // filter using the current cell value
    //       });
    //       event.api.onFilterChanged(); // Apply the filter
    //     }
    //   }
    // },

    //suppressColumnStateEvents: true,
    //suppressLocalStorage: true,
    //  maxBlocksInCache: 1
    //   enableColResize: true,
    //   enableColumnsToolPanel: true,
    //   enableSorting: false,
  };

  // const onToolPanelVisible = (event) => {
      //   const toolPanelVisible = event.toolPanels.filter(panel => panel.id === 'columns')[0].visible;
  //   if (!toolPanelVisible) {
  //     // Custom logic when the column tool panel is closed
  //     console.log('Column Tool Panel closed');
  //   }
  // };


  const onToolPanelVisibleChanged = useCallback((event) => {
    const columnState = event.columnApi.getColumnState();
    syncedLayoutRef.current = JSON.stringify(columnState);
    saveLayout(columnState);
  }, [saveLayout]);

  //const handleFilterChanged2 = () => {
  //  console.log('filter changed');

  //   const columnDefs = gridApi.getColumnDefs();

  //   const newFilterValues = { ...filterValues }; // Create a copy of existing filter values

  //   columnDefs.forEach(columnDef => {
  //       if (columnDef.field && columnDef.yesNoToBoolean) {

  //           console.log('convert yes/no to boolean');
  //           const filterInstance = gridApi.getFilterInstance(columnDef.field); // Get filter instance for specific column
  //           const filterModel = filterInstance.getModel(); // Get filter model

  //           // Convert 'Yes' and 'No' values to boolean
  //           const convertedValues = filterModel.values.map(value => value === 'Yes');

  //           // Update newFilterValues object with converted values for the targeted column
  //           newFilterValues[columnDef.field] = convertedValues;
  //       } else if (columnDef.field) {
  //           // For other columns, keep the existing filter values
  //           const filterInstance = gridApi.getFilterInstance(columnDef.field); // Get filter instance for specific column
  //           const filterModel = filterInstance.getModel(); // Get filter model
  //           newFilterValues[columnDef.field] = filterModel.values;
  //       }
  //  });

  //   // Update filterValues state with new values
  //   setFilterValues(newFilterValues);
  //};

  // const onToolPanelSizeChanged = useCallback((event) => {
  //   console.log("toolPanelSizeChanged", event);
  // }, []);


  const getCustomDateRange = (typeOfFilter) => {

    //console.log('inside custom date range');
    //console.log(typeOfFilter);

    if (typeOfFilter === '') return {};
  
    const today = new Date();

    let dateFrom = today.toISOString().split("T")[0]; // YYYY-MM-DD format
    let dateTo = today.toISOString().split("T")[0]; // YYYY-MM-DD format

    switch (typeOfFilter) {
      case 'This Year':
        dateFrom = (new Date(today.getFullYear(), 0, 1)).toISOString().split("T")[0]
        dateTo = (new Date(today.getFullYear(), 11, 31)).toISOString().split("T")[0];
        break;
      case 'Last Year':
        dateFrom = (new Date(today.getFullYear()-1, 0, 1)).toISOString().split("T")[0];
        dateTo = (new Date(today.getFullYear()-1, 11, 31)).toISOString().split("T")[0];
        break;
      case 'This Month':
        dateFrom = (new Date(today.getFullYear(), today.getMonth(), 1)).toISOString().split("T")[0];
        dateTo = (new Date(today.getFullYear(), today.getMonth() + 1, 0)).toISOString().split("T")[0];
        break;
      case 'Last Month': 
        dateFrom = (new Date(today.getFullYear(), today.getMonth() - 1, 1)).toISOString().split("T")[0];
        dateTo = (new Date(today.getFullYear(), today.getMonth(), 0)).toISOString().split("T")[0];
        break;
      case 'This Week': 
        today.setDate(today.getDate() - today.getDay());
        dateFrom = today.toISOString().split("T")[0];
        today.setDate(today.getDate() + 6)
        dateTo = today.toISOString().split("T")[0];
        break;
      case 'Last Week':
        today.setDate(today.getDate() - today.getDay() - 7);
        dateFrom = today.toISOString().split("T")[0];
        today.setDate(today.getDate() + 6)
        dateTo = today.toISOString().split("T")[0];
      default:
        break;
    };

    return { filterType: 'date', type: typeOfFilter, dateFrom, dateTo};

  };
  const onFilterModified = useCallback ( (params) => {

    const filterGui = params.filterInstance.getGui(); // Get the filter UI element
    const dropdown = filterGui.querySelector('.ag-picker-field-display'); // Find the div with selected value

    if (!dropdown) return;
    
    const typeOfFilter = dropdown.textContent.trim();

    if (dateFixedFilters.includes(typeOfFilter)) {
      params.filterInstance.setModel(getCustomDateRange(typeOfFilter));
      params.api.onFilterChanged(); // Apply the changes
    }

  }, []);

  // if (!itemsPerPage || !currUrlSearchParams || !currentPage || !additionalFilters)
  // {
  //   return null;
  // }
  // console.log('Before paginatedGridWrapper UI Rendered');


  const normalRowHeight = useMemo(() => (
    Number.isFinite(Number(props.rowHeight)) ? Number(props.rowHeight) : 20
  ), [props.rowHeight]);

  const pinnedRowHeight = useMemo(() => (
    Number.isFinite(Number(props.pinnedRowHeight)) ? Number(props.pinnedRowHeight) : 32
  ), [props.pinnedRowHeight]);

  const getRowHeight = useCallback((params) => {
    if (params?.node?.rowPinned) {
      return pinnedRowHeight;
    }
    return normalRowHeight;
  }, [normalRowHeight, pinnedRowHeight]);

  const getRowStyle = useCallback((params) => {
    if (params?.node?.rowPinned) {
      return {lineHeight: `${pinnedRowHeight}px`};
    }
    return null;
  }, [pinnedRowHeight]);


  return (
    <AlpineContainer isTabOffset={props?.isTabOffset}  > 
      <AgGridReact
        {...gridDefaultOptions}
        pagination={true}
        rowModelType={'serverSide'}
        paginationPageSize={itemsPerPage}
        cacheBlockSize = {itemsPerPage}
        serverSideStoreType={'partial'}
        getContextMenuItems={props.contextMenu}
        pinnedBottomRowData={props.pinnedBottomRowData}
        getRowHeight={getRowHeight}
        getRowStyle={getRowStyle}
        columnDefs={orderedColumnDefs}
        rowData={props?.rowData || []}
        frameworkComponents={props.components}
        onGridReady={onGridReady}
        //enableSorting={true}
        suppressPaginationPanel={true}
        onCellDoubleClicked={onCellDoubleClicked}
        masterDetail={!!(props.masterDetail && !!props?.detailsCellRenderer)}
        detailCellRenderer={props.detailsCellRenderer}
        onFirstDataRendered={props.masterDetail ? onFirstDataRendered : null}
        gridOptions={gridOptions}
        enableColumnChooser={true}
        onToolPanelVisibleChanged={onToolPanelVisibleChanged}    
        //onFilterOpened={onFilterOpened}
        onFilterModified={onFilterModified}
        onFilterChanged={onFilterChanged}
        {...(props.serverGroup ? props.serverGroup.gridOptions : {})}
        // context={{
        //   closeRequirementFilter: closeRequirementFilter
        // }}
      />
      <div style={{ display: 'none' }}>
         <div className="ag-theme-alpine" style={{ height: 0, width: 0 }}>
           <AgGridReact
             columnDefs={orderedColumnDefs}
             rowData={[]}
             onGridReady={onTempGridReady}
           />
         </div>
       </div>
    </AlpineContainer>

  );
};

MyPaginatedGrid.propTypes = {
  ls_name: PropTypes.string.isRequired,
  columnDefs: PropTypes.instanceOf(Object).isRequired,
  //contextMenu: PropTypes.instanceOf(Array).isRequired,
  contextMenu: PropTypes.instanceOf(Function).isRequired,  // Thanh 05/01/24
  fetchParams: PropTypes.shape({
    url: PropTypes.string.isRequired,
    respKey:  PropTypes.string.isRequired,
    //respKey:  PropTypes.instanceOf(String).isRequired,
  }),
  modifyDataElement: PropTypes.instanceOf(Function),
  onFetchFail: PropTypes.instanceOf(Function),

  //rowData: PropTypes.instanceOf(Array).isRequired,  // Thanh 05/01/24
  components: PropTypes.instanceOf(Object),
  registerGrid: PropTypes.func.isRequired,
  pagination: PropTypes.shape({
    setTotalPageCount: PropTypes.func.isRequired,
    setTotalItemsCount: PropTypes.func.isRequired,
    setCurrentPage: PropTypes.func.isRequired,
    currentPage: PropTypes.number,
    itemsPerPage: PropTypes.number.isRequired,
  }).isRequired,
  companyId: PropTypes.string,
  onCellDoubleClicked: PropTypes.func,
  //defFilterParams: PropTypes.instanceOf(Array).isRequired,
  additionalFilters: PropTypes.instanceOf(Object),
  masterDetail: PropTypes.bool,
  detailsCellRenderer: PropTypes.instanceOf(Function),
  refreshTimestamp: PropTypes.number,
  /**
   * Server-side row grouping. The server answers a request without the group key with the
   * groups, and one with it with that group's rows. Must be a stable object (module constant):
   * a new one on each render resets the datasource.
   */
  serverGroup: PropTypes.shape({
    /** Request param that carries the key of the group being opened. */
    keyParam: PropTypes.string.isRequired,
    /** Extra params sent with every request, e.g. the flag that turns grouping on. */
    requestParams: PropTypes.instanceOf(Object),
    /** AG Grid options for the grouping (groupDisplayType, getChildCount, ...). */
    gridOptions: PropTypes.instanceOf(Object),
  }),
};

MyPaginatedGrid.defaultProps = {
  components: {},
  onFetchFail: ()=>{console.log('Fail to fetch data...');},
  modifyDataElement: undefined,
    fetchParams: {
    apiSource: 'iPermit',
  },
  companyId: undefined,
  onCellDoubleClicked: undefined,
  additionalFilters: {},
  masterDetail: false,
  detailsCellRenderer: undefined,
  refreshTimestamp: null,
  serverGroup: undefined,
};
export default MyPaginatedGrid;


